import { useEffect, useState } from "react";
import {
  ApiError,
  api,
  type CurrentPeriodResponse,
  type SubscriptionResponse
} from "../api/client";
import { InfoTooltip } from "../components/Tooltip";
import { formatCents, formatTier, tierTagline } from "../lib/format";

function formatStripeStatus(status: string | undefined): string {
  if (!status) return "—";
  return status
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

function formatStripeUnixDate(ts: number | null): string {
  if (ts === null) return "—";
  return new Date(ts * 1000).toISOString().slice(0, 10);
}

export function BillingPage(props: { onChangePlan: () => void }) {
  const [period, setPeriod] = useState<CurrentPeriodResponse | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, s] = await Promise.all([api.billingCurrentPeriod(), api.billingSubscription()]);
        if (cancelled) return;
        setPeriod(p);
        setSubscription(s);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.code === "billing_not_configured") {
            setError(
              "Billing isn't configured on this deployment yet. Once Stripe is wired up, this page will show your subscription and payment methods."
            );
          } else {
            setError(err.message);
          }
        } else {
          setError("Could not load billing.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function openPortal() {
    setPortalBusy(true);
    try {
      const result = await api.billingPortal();
      window.location.href = result.portal_url;
    } catch (err) {
      setPortalBusy(false);
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not open Stripe portal.");
    }
  }

  if (error && !period && !subscription) {
    return (
      <div className="panel">
        <h2 className="section-title">Billing</h2>
        <div className="error">{error}</div>
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button className="btn" onClick={props.onChangePlan}>
            Change plan
          </button>
        </div>
      </div>
    );
  }
  if (!period) return <p className="muted">Loading…</p>;

  const isFree = period.tier === "free";
  const subStatus = subscription?.stripe_subscription?.status;
  const accountStatus = subscription?.account_status ?? "active";

  return (
    <>
      <div className="panel">
        <h2 className="section-title">Plan</h2>
        <dl>
          <div className="row">
            <dt>Current plan</dt>
            <dd>
              {formatTier(period.tier)}
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                {tierTagline(period.tier)}
              </div>
            </dd>
          </div>
          <div className="row">
            <dt>
              Pricing model
              <InfoTooltip content="per_signature charges for each witness signature past the included allotment. flat_rate is a monthly plan with a generous included allotment and a lower overage rate." />
            </dt>
            <dd>{period.pricing_model}</dd>
          </div>
          <div className="row">
            <dt>
              Log retention
              <InfoTooltip content="How long Sequesign keeps your audit data after it's recorded. Longer retention costs more per GB-month." />
            </dt>
            <dd>{period.retention_target}</dd>
          </div>
          <div className="row">
            <dt>Subscription status</dt>
            <dd>
              {isFree
                ? "No subscription"
                : subStatus
                  ? formatStripeStatus(subStatus)
                  : formatStripeStatus(accountStatus)}
            </dd>
          </div>
          {subscription?.stripe_subscription ? (
            <>
              <div className="row">
                <dt>Current billing period</dt>
                <dd>
                  {formatStripeUnixDate(subscription.stripe_subscription.current_period_start)} →{" "}
                  {formatStripeUnixDate(subscription.stripe_subscription.current_period_end)}
                </dd>
              </div>
              {subscription.stripe_subscription.cancel_at_period_end ? (
                <div className="row">
                  <dt>Scheduled change</dt>
                  <dd>Cancels at period end.</dd>
                </div>
              ) : null}
            </>
          ) : null}
        </dl>
        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <button className="btn" onClick={props.onChangePlan}>
            Change plan
          </button>
          {isFree ? (
            <button className="btn btn-secondary" onClick={props.onChangePlan}>
              Add a payment method
            </button>
          ) : (
            <button className="btn btn-secondary" disabled={portalBusy} onClick={openPortal}>
              {portalBusy ? "Opening…" : "Manage in Stripe"}
            </button>
          )}
        </div>
      </div>

      <div className="panel">
        <h2 className="section-title">
          Next invoice
          <InfoTooltip content="Estimate based on current usage and your tier rates. The actual invoice is generated by Stripe at period close and may include proration, taxes, or minimum-charge handling." />
        </h2>
        {isFree ? (
          <p className="muted">
            Free tier never produces an invoice. Upgrade to add a payment method and start metered
            billing.
          </p>
        ) : period.estimated_charges.line_items.length === 0 ? (
          <p className="muted">
            No charges yet this period. Charges accrue as your agents sign actions.
          </p>
        ) : (
          <dl>
            {period.estimated_charges.line_items.map((item, i) => (
              <div className="row" key={i}>
                <dt>{item.description}</dt>
                <dd>{formatCents(item.amount_cents)}</dd>
              </div>
            ))}
            <div className="row">
              <dt>
                <strong>Estimated total</strong>
              </dt>
              <dd>
                <strong>{formatCents(period.estimated_charges.total_cents)}</strong>
              </dd>
            </div>
          </dl>
        )}
      </div>

      <div className="panel">
        <h2 className="section-title">Payment method</h2>
        {isFree ? (
          <p className="muted">No payment method on file. Free tier doesn't require one.</p>
        ) : (
          <p className="muted">
            Payment methods are managed in the Stripe customer portal. Click "Manage in Stripe"
            above to view, add, or remove cards.
          </p>
        )}
      </div>

      <div className="panel">
        <h2 className="section-title">Invoice history</h2>
        {isFree ? (
          <p className="muted">No invoices yet. Free tier doesn't generate invoices.</p>
        ) : (
          <p className="muted">
            Past invoices, including downloadable PDFs, are available in the Stripe customer portal.
            Click "Manage in Stripe" above.
          </p>
        )}
      </div>
      {error ? <div className="error">{error}</div> : null}
    </>
  );
}
